import {
    Dispatch,
    SetStateAction,
    MouseEvent as ReactMouseEvent,
    MutableRefObject,
    useCallback,
    useMemo,
    useState,
} from 'react'
import { dragExceededThreshold } from '../../elements/coords'
import {
    elementsInMarquee,
    primarySelectedId,
    resolveSelectableId,
    selectAllTopLevelIds,
    toggleIdInSelection,
} from '../../elements/selection'
import { defaultEditorTheme } from '../../templateEditor.theme'
import { Rect, TemplateElement } from '../../templateEditor.types'
import { startPointerSession } from './documentPointer'

const theme = defaultEditorTheme

type Params = {
    elements: TemplateElement[]
    selectedIds: string[]
    setSelectedIds: Dispatch<SetStateAction<string[]>>
    zoomRef: MutableRefObject<number>
}

export const useTemplateEditorSelection = ({ elements, selectedIds, setSelectedIds, zoomRef }: Params) => {
    const [marqueeRect, setMarqueeRect] = useState<Rect | null>(null)

    const selectedId = primarySelectedId(selectedIds)
    const selectedElement = useMemo(() => elements.find((element) => element.id === selectedId) || null, [
        elements,
        selectedId,
    ])

    const selectElement = useCallback(
        (id: string, additive = false, exact = false) => {
            const resolved = exact ? id : resolveSelectableId(elements, id)
            setSelectedIds((prev) => (additive ? toggleIdInSelection(prev, resolved) : [resolved]))
        },
        [elements, setSelectedIds]
    )

    const clearSelection = useCallback(() => {
        setSelectedIds([])
    }, [setSelectedIds])

    const selectAll = useCallback(() => {
        setSelectedIds(selectAllTopLevelIds(elements))
    }, [elements, setSelectedIds])

    const startMarqueeSelect = useCallback(
        (event: ReactMouseEvent, frameEl: HTMLElement) => {
            if (event.button !== 0) {
                return
            }

            const frameRect = frameEl.getBoundingClientRect()
            const scale = theme.displayScale * zoomRef.current
            const originX = (event.clientX - frameRect.left) / scale
            const originY = (event.clientY - frameRect.top) / scale
            const startClientX = event.clientX
            const startClientY = event.clientY
            let moved = false

            if (!event.shiftKey) {
                setSelectedIds([])
            }

            startPointerSession(
                (moveEvent) => {
                    if (
                        !moved &&
                        !dragExceededThreshold(
                            startClientX,
                            startClientY,
                            moveEvent.clientX,
                            moveEvent.clientY,
                            theme.dragThreshold
                        )
                    ) {
                        return
                    }
                    moved = true

                    const endX = (moveEvent.clientX - frameRect.left) / scale
                    const endY = (moveEvent.clientY - frameRect.top) / scale
                    setMarqueeRect({
                        x: Math.min(originX, endX),
                        y: Math.min(originY, endY),
                        w: Math.abs(endX - originX),
                        h: Math.abs(endY - originY),
                    })
                },
                () => {
                    setMarqueeRect((current) => {
                        if (current && moved) {
                            const ids = elementsInMarquee(elements, current)
                            setSelectedIds((prev) => (event.shiftKey ? Array.from(new Set([...prev, ...ids])) : ids))
                        }
                        return null
                    })
                }
            )
        },
        [elements, setSelectedIds, zoomRef]
    )

    return {
        selectedId,
        selectedElement,
        marqueeRect,
        selectElement,
        clearSelection,
        selectAll,
        startMarqueeSelect,
    }
}
